/**
 * @author NikolaV
 */
import { makeObservable, observable } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { DeleteMaterialDialog } from '@pages/trucks/ui/DeleteMaterialDialog';
import { saveAs } from 'file-saver';


export class MaterialsStore implements ICrudStore<IMaterial> {
    @observable items: IMaterial[] = [];
    @observable isDoingAsyncWork: boolean = false;

    constructor(
        private geofencesStore: any
    ) {
        makeObservable(this);
    }

    fetch = () => {
        this.isDoingAsyncWork = true;

        const materialsPromise = Ajax({ url: '/api/project_materials.json' });

        return materialsPromise.then(resp => {
                resp.materials = (resp.materials || []).sort((m1, m2) => alphaNumSort(m1.material, m2.material));
                this.items = resp.materials;
            })
            .always(() => this.isDoingAsyncWork = false);
    };

    create = (material: IMaterial) => {
        const materialName = material.material;
        if (materialName.trim().length === 0) return false;

        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'api/add_material_to_project.json',
            method: 'post',
            processData: false,
            data: {
                materialName,
                projectId: kuura.getProjectId(),
            },
        }).done(({ material }) => {
            this.items.unshift({
                id: material.id,
                material: material.material,
            });
        }).fail(Notify.error)
          .always(() => this.isDoingAsyncWork = false);
    };

    update = (material: IMaterial) => {
        const materialId: number = material.id;
        const newName: string = material.material;

        const existingMaterial: IMaterial = this.items.find(m => m.id === materialId);
        const oldName: string = existingMaterial.material;

        if (this.items.find(m => m.material === newName)) {
            Notify.error('Material already exists');
            return false;
        }

        existingMaterial.material = newName;

        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'api/edit_project_material.json',
            method: 'post',
            processData: false,
            data: {
                materialId,
                materialName: newName,
            },
        }).done(Notify.success)
          .fail(() => {
              existingMaterial.material = oldName;
              Notify.error();
          })
          .always(() => this.isDoingAsyncWork = false);
    };

    removeAndMerge = (itemId: number) => {
        if (this.items.length === 1) {
            Notify.warn(__('trucks.materials.delete_material_no_last'));
            return false;
        }

        if (!this.isMaterialDeletable(itemId)) {
            Notify.warn(__('trucks.materials.delete_material_assigned'), 10);
            return false;
        }

        if (!$.isNumeric(itemId)) return false;

        DeleteMaterialDialog.open(this, itemId, this.getAreasContainingMaterial(itemId), () => {
            this.items.splice(this.items.findIndex(m => m.id === itemId), 1);
        });
    };

    isMaterialDeletable(materialId) {
        if (!this.geofencesStore) {
            console.warn('geofencesStore doesnt exist');
            return false;
        }
        return !this.geofencesStore.items.find(g => g.materialId == materialId);
    }

    getMaterialName(materialId: number): string {
        if (materialId === -1) {
            return '-1';
        }
        const mat = this.items.find(m => m.id === materialId);
        return mat ? mat.material : '';
    }

    getAreasContainingMaterial(materialId: number): string[] {
        if (!this.geofencesStore) {
            console.warn('geofencesStore doesnt exist');
            return [];
        }

        if (materialId === -1) {
            return [];
        }
        let itemNames = [];
        this.geofencesStore.items.forEach(item => {
            for (const material of item.materials) {
                if (material.id === materialId) {
                    itemNames.push(item.title);
                    break;
                }
            }
        });
        return itemNames;
    }

    isMaterialInTrip = (materialId: number) => {
        return Ajax({ url: `/material/${materialId}/trips` });
    };

    importMaterials = (file: File) => {
        this.isDoingAsyncWork = true;

        const data = new FormData()
        data.append('data', file)

        return Ajax({
            url: `api/1/project/${kuura.getProjectUuid()}/materials`,
            method: 'post',
            processData: false,
            data
        })
            .then(this.fetch)
            .always(() => this.isDoingAsyncWork = false);
    }

    exportMaterials = () => {
        let csv = `material\n`;
        this.items.forEach((material) => {
            csv += (material.material+"\n")
        })

        const file = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(file, `${kuura.getProjectName()} materials.csv`);
    }
}
